// @ts-nocheck
/* eslint-disable */

"use client";

import {
  Button,
  Column,
  FormField,
  Heading,
  Icon,
  RichText,
  Row,
  Separator,
} from "daskapital-ds";
import React, { useEffect, useState } from "react";
import {
  StyledBubble,
  calculateRentPromoterMaxDiscount,
  calculateRentPromoterMaxTotalCost,
  calculateRentPromoterMinReceived,
  calculateRentPromoterServiceFee,
  calculateRentPromoterTotalAmount,
  formatAsCurrency,
  formsAttrs,
} from "./simulator.utils";

import Link from "next/link";
import { NumericFormat } from "react-number-format";
import { useGlobalContext } from "@/lib/globalContext";
import Tooltip from "../Tooltip/Tooltip";
import Newsletter from "../Newsletter";

export default function AdvanceRent({
  getStarted,
  knowMore,
  title,
  description,
  backgroundColor,
  textColor,
  termsDescription,
  primaryButtonText,
  secondaryButtonText,
  showNotifyMe,
}: formsAttrs) {
  const state = useGlobalContext();
  const shouldShowNotifyMe = showNotifyMe ?? false;

  const [fields, setFields] = useState<Record<string, string | number>>({
    numberOfRents: 3,
    rentValue: 0,
    discount: 10,
  });
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [minReceived, setMinReceived] = useState<number>(0);
  const [maxDiscount, setMaxDiscount] = useState<number>(0);
  const [serviceFee, setServiceFee] = useState<number>(0);
  const [maxTotalCost, setMaxTotalCost] = useState<number>(0);

  const setValue = (key: string, value: string | number) => {
    setFields({ ...fields, [key]: value });
  };

  const calcTotal = () => {
    if (!fields.rentValue || !fields.numberOfRents || !fields.discount) {
      setTotalAmount(0);
      setMinReceived(0);
      setMaxDiscount(0);
      setServiceFee(0);
      setMaxTotalCost(0);
      return;
    }

    const params = {
      numberOfRents: Number(fields.numberOfRents),
      rentValue: Number(fields.rentValue),
      discount: Number(fields.discount),
    };

    setTotalAmount(calculateRentPromoterTotalAmount(params));
    setMinReceived(calculateRentPromoterMinReceived(params));
    setMaxDiscount(calculateRentPromoterMaxDiscount(params));
    setServiceFee(calculateRentPromoterServiceFee(params));
    setMaxTotalCost(calculateRentPromoterMaxTotalCost(params));
  };

  useEffect(() => {
    calcTotal();
  }, [fields]);

  return (
    <div
      className="block-group radius-md"
      style={{
        padding: "16px",
        overflow: "hidden",
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(textColor ? { color: textColor } : {}),
      }}
    >
      <Row fluid={true} className="m-0">
        <Column lg={8}>
          <div className="mb-8 mb-md-0 pe-md-8">
            <Heading size={"h3"} tag={"h4"}>
              {title || state?.dict?.custom?.advanceRent}
            </Heading>
            <Heading size={"texts"} tag={"span"} className="font-alternative mt-3">
              {description || state?.dict?.custom?.advanceRentTxt}
            </Heading>

            {shouldShowNotifyMe && (
              <div className="d-flex bg-gray10 radius-sm p-4 mt-5">
                <Newsletter
                  label={state?.dict?.custom?.notifyAvailableTitle}
                  placeholder={state?.dict?.custom?.notifyAvailablePlaceholder}
                  buttonText={state?.dict?.custom?.notifyAvailableButton}
                />
              </div>
            )}

            <div className="mt-7">
              <Heading size={"texts"} tag={"span"} className="font-alternative">
                {state?.dict?.custom?.rentValueTitle}
              </Heading>
              <div className="mt-2">
                <FormField id="rentValue" className="mb-0">
                  <NumericFormat
                    thousandSeparator=" "
                    decimalSeparator=","
                    suffix={"€"}
                    decimalScale={2}
                    allowNegative={false}
                    name="rentValue"
                    placeholder={state?.dict?.custom?.amount}
                    className="font-alternative w-100"
                    onChange={(e) => {
                      setValue(
                        "rentValue",
                        parseFloat(
                          e.target.value.replaceAll(" ", "").replaceAll(",", "."),
                        ),
                      );
                    }}
                  />
                </FormField>
              </div>
            </div>

            <div className="mt-6">
              <Heading size={"texts"} tag={"span"} className="font-alternative">
                {state?.dict?.custom?.numberOfRentsTitle}
              </Heading>
              <div className="d-flex align-items-center mt-2" style={{ gap: "1rem" }}>
                <div className="flex-grow-1">
                  <FormField id="numberOfRents" className="mb-0">
                    <input
                      type={"range"}
                      name="numberOfRents"
                      className="w-100"
                      min={3}
                      max={12}
                      step={1}
                      defaultValue={3}
                      onChange={(e) => {
                        e.preventDefault();
                        setValue("numberOfRents", Number(e.target.value));
                      }}
                    />
                  </FormField>
                </div>
                <div className="flex-shrink-1">
                  <StyledBubble type="text" className="texts">
                    {fields.numberOfRents}
                  </StyledBubble>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <Tooltip
                content={state?.dict?.custom?.rentDiscountTooltip}
                displayIcon={true}
                place="top"
              >
                <Heading size={"texts"} tag={"span"} className="font-alternative">
                  {state?.dict?.custom?.discountTitle}
                </Heading>
              </Tooltip>
              <div className="d-flex align-items-center mt-2" style={{ gap: "1rem" }}>
                <div className="flex-grow-1">
                  <FormField id="discount" className="mb-0">
                    <input
                      type={"range"}
                      name="discount"
                      className="w-100"
                      min={5}
                      max={20}
                      step={1}
                      defaultValue={10}
                      onChange={(e) => {
                        setValue("discount", Number(e.target.value));
                      }}
                    />
                  </FormField>
                </div>
                <div className="flex-shrink-1">
                  <StyledBubble type="text" className="texts">
                    {fields.discount}%
                  </StyledBubble>
                </div>
              </div>
            </div>

            <div className="d-flex bg-gray10 radius-sm p-4 mt-9">
              <Icon icon="icon-info" className="color-primary50 mt-1 me-2" />
              <span className="d-block">
                <p className="color-gray90">
                  {termsDescription || state?.dict?.custom?.termsAdvanceRent}
                </p>
              </span>
            </div>
          </div>
        </Column>
        <Column lg={4} className="bg-gray70 radius-sm">
          <div
            className="d-flex justify-content-between p-5 h-100 simulator-totals"
            style={{ flexFlow: "column" }}
          >
            <div className="color-white">
              <Heading size={"texts"} tag={"h4"} className="font-alternative mb-4">
                {state?.dict?.custom?.rentMinReceived}
              </Heading>

              <Heading size={"h2"} tag={"span"} className="color-secondary50 mb-4">
                {formatAsCurrency(minReceived)}
              </Heading>

              <Heading size={"texts"} tag={"span"} className="font-alternative mb-4">
                {state?.dict?.custom?.rentTotalAmount?.replaceAll(
                  "{{value}}",
                  formatAsCurrency(totalAmount),
                )}
              </Heading>

              <Separator color="var(--color-white)" height="1px" className="mb-5" />

              <Heading size={"texts"} tag={"span"} className="font-alternative mb-4">
                {state?.dict?.custom?.rentMaxTotalCost}
              </Heading>
              <Heading size={"h4"} tag={"span"} className="color-secondary50 mb-4">
                {formatAsCurrency(maxTotalCost)}
              </Heading>

              <p style={{ fontSize: "16px", lineHeight: "170%" }} className="d-flex flex-row justify-content-between">
                <b>{state?.dict?.custom?.rentMaxDiscount}</b>
                {formatAsCurrency(maxDiscount)}
              </p>
              <p style={{ fontSize: "16px", lineHeight: "170%" }} className="d-flex flex-row justify-content-between">
                <b>{state?.dict?.custom?.rentServiceFee}</b>
                {formatAsCurrency(serviceFee)}
              </p>
            </div>
            <div className="text-center mt-6">
              <Link
                href={
                  getStarted?.url
                    ? getStarted?.url
                    : "https://platform.daskapital.eu/register"
                }
                target="_blank"
                rel="noreferrer noopener"
                className="d-block"
              >
                <Button variant="secondary" className="justify-content-center w-100">
                  {primaryButtonText || state.dict?.custom?.getStarted}
                </Button>
              </Link>
              <Link
                href={knowMore?.url ? knowMore?.url : "/contacts"}
                target="_blank"
                rel="noreferrer noopener"
                className="d-inline-block"
              >
                <Button variant="link" className="color-white">
                  {secondaryButtonText || state.dict?.common?.knowMore}
                </Button>
              </Link>
            </div>
          </div>
        </Column>
      </Row>
    </div>
  );
}
